import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FolderOpen } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Step, SequenceTemplate } from "./types";
import { TemplateSelector } from "./TemplateSelector";
import { useSequenceOperations } from "@/hooks/useSequenceOperations";
import { DAILY_NEWSLETTER_SEQUENCE } from "./templates/newsletter-sequence/DailyNewsletterSequence";
import { WELCOME_SEQUENCE_TEMPLATE } from "./templates/welcome-sequence/WelcomeSequence";
import { SequenceForm } from "./components/SequenceForm";

interface SequenceBuilderProps {
  defaultTemplate?: 'welcome' | 'newsletter' | 'custom';
}

export const SequenceBuilder = ({ defaultTemplate }: SequenceBuilderProps) => {
  const { toast } = useToast();
  const { saveSequence, isSaving } = useSequenceOperations();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [steps, setSteps] = useState<Step[]>([]);
  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);

  useEffect(() => {
    if (defaultTemplate === 'welcome') {
      setName(WELCOME_SEQUENCE_TEMPLATE.name);
      setDescription(WELCOME_SEQUENCE_TEMPLATE.description);
      setSteps(WELCOME_SEQUENCE_TEMPLATE.steps);
    } else if (defaultTemplate === 'newsletter') {
      setName(DAILY_NEWSLETTER_SEQUENCE.name);
      setDescription(DAILY_NEWSLETTER_SEQUENCE.description);
      setSteps(DAILY_NEWSLETTER_SEQUENCE.steps);
    } else {
      setName("");
      setDescription("");
      setSteps([]);
    }
  }, [defaultTemplate]);

  const loadTemplate = (template: SequenceTemplate) => {
    setName(template.name);
    setDescription(template.description);
    setSteps(template.steps);
    
    toast({
      title: "Template Loaded",
      description: `${template.name} template has been loaded successfully.`,
    });
    setShowTemplateSelector(false);
  };

  const addStep = () => {
    const newStep: Step = {
      step_number: steps.length + 1,
      channel: "email",
      content_template: {
        subject: "",
        body: "",
      },
      delay_hours: 24,
    };
    setSteps([...steps, newStep]);
  };

  const removeStep = (index: number) => {
    const updatedSteps = steps.filter((_, i) => i !== index).map((step, i) => ({
      ...step,
      step_number: i + 1
    }));
    setSteps(updatedSteps);
  };

  const updateStep = (index: number, field: string, value: any) => {
    const updatedSteps = [...steps];
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      updatedSteps[index] = {
        ...updatedSteps[index],
        [parent]: {
          ...updatedSteps[index][parent],
          [child]: value
        }
      };
    } else {
      updatedSteps[index] = {
        ...updatedSteps[index],
        [field]: value
      };
    }
    setSteps(updatedSteps);
  };

  const handleSave = async () => {
    if (!name || !description || steps.length === 0) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields and add at least one step.",
        variant: "destructive",
      });
      return;
    }

    try {
      await saveSequence(name, description, steps, selectedLeads);
      toast({
        title: "Success",
        description: "Sequence saved successfully",
      });
    } catch (error) {
      console.error("Error in handleSave:", error);
      toast({
        title: "Error",
        description: "Failed to save sequence. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {defaultTemplate 
            ? `Configure ${defaultTemplate.charAt(0).toUpperCase() + defaultTemplate.slice(1)} Sequence` 
            : 'Create Sequence'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {!defaultTemplate && (
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => setShowTemplateSelector(true)}
              className="flex items-center gap-2"
            >
              <FolderOpen className="h-4 w-4" />
              Load Template
            </Button>
          </div>
        )}

        {showTemplateSelector && (
          <TemplateSelector
            onTemplateSelect={loadTemplate}
            onClose={() => setShowTemplateSelector(false)}
          />
        )}

        <SequenceForm
          name={name}
          description={description}
          steps={steps}
          selectedLeads={selectedLeads}
          isSaving={isSaving}
          onNameChange={setName}
          onDescriptionChange={setDescription}
          onRemoveStep={removeStep}
          onUpdateStep={updateStep}
          onAddStep={addStep}
          onLeadsChange={setSelectedLeads}
          onSave={handleSave}
        />
      </CardContent>
    </Card>
  );
};